import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileSpreadsheet,
  UploadCloud,
  X,
  FileCheck,
  AlertCircle,
  Building2,
  ChevronDown,
  CheckCircle2,
  Calendar,
  ShieldCheck,
  Loader2,
  Package,
  Layers,
  Lock,
  Download,
  HelpCircle,
  ArrowRight,
  Info,
} from "lucide-react";
import toast from "react-hot-toast";
import SummaryApi from "../common";

const UploadExcel = () => {
  const [file, setFile] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [subscriptions, setSubscriptions] = useState([]);
  const [hasActivePlan, setHasActivePlan] = useState(false);
  const [isLoadingSubs, setIsLoadingSubs] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [displayCount, setDisplayCount] = useState(0);

  const fileInputRef = useRef(null);

  const fetchActiveOrganizations = useCallback(async () => {
    try {
      const response = await fetch(SummaryApi.getActiveOrganization.url, {
        method: SummaryApi.getActiveOrganization.method,
      });
      const result = await response.json();
      if (result.status && Array.isArray(result.data))
        setOrganizations(result.data);
    } catch (error) {
      toast.error("Failed to load organizations");
    }
  }, []);

  const fetchSubscriptions = useCallback(async (orgId) => {
    if (!orgId) {
      setSubscriptions([]);
      setHasActivePlan(false);
      return;
    }
    setIsLoadingSubs(true);
    try {
      const response = await fetch(
        SummaryApi.getActiveOrganizationSubscription.url,
        {
          method: SummaryApi.getActiveOrganizationSubscription.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ organization_id: orgId }),
        },
      );
      const result = await response.json();

      if (result.status && result.data.length > 0) {
        const active = result.data.some((sub) => sub.status === "Active");
        setSubscriptions(result.data);
        setHasActivePlan(active);
      } else {
        setSubscriptions([]);
        setHasActivePlan(false);
      }
    } catch (error) {
      console.error("Subs Fetch Error:", error);
    } finally {
      setIsLoadingSubs(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveOrganizations();
  }, [fetchActiveOrganizations]);
  useEffect(() => {
    fetchSubscriptions(selectedOrgId);
  }, [selectedOrgId, fetchSubscriptions]);

  const validateAndSetFile = (selectedFile) => {
    if (!selectedFile) return;
    const ext = selectedFile.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      toast.error("Invalid file format. Please use .xlsx or .csv");
      return;
    }
    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!selectedOrgId || !file || !hasActivePlan) return;
    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("excel_file", file);
    formData.append("organization_id", selectedOrgId);

    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => (prev < 90 ? prev + 5 : prev));
    }, 200);

    try {
      const response = await fetch(SummaryApi.uploadExcel.url, {
        method: SummaryApi.uploadExcel.method,
        body: formData,
      });
      const result = await response.json();
      clearInterval(progressInterval);

      if (result.status) {
        setUploadProgress(100);

        // 1. Get the Active Subscription details from the state
        const activeSub = subscriptions.find((sub) => sub.status === "Active");
        const insertedUserIds = result.inserted_user_ids || [];

        // 2. If there are new users and an active plan, trigger subscriptions
        if (activeSub && insertedUserIds.length > 0) {
          await triggerBulkSubscriptions(insertedUserIds, activeSub);
        }

        // 3. Show Result Toast
        const createdCount = parseInt(result.message.match(/\d+/)) || 0;
        toast.custom(
          (t) => (
            <UploadResultToast
              t={t}
              createdCount={createdCount}
              duplicateCount={result.duplicates?.count || 0}
              duplicateData={result.duplicates?.data || []}
              message={result.message}
            />
          ),
          { duration: 6000, position: "top-right" },
        );

        setTimeout(() => {
          setFile(null);
          setIsUploading(false);
        }, 1500);
      } else {
        setIsUploading(false);
        toast.error(result.message || "Upload failed");
      }
    } catch (error) {
      clearInterval(progressInterval);
      setIsUploading(false);
      toast.error("Network error occurred");
    }
  };
  const triggerBulkSubscriptions = async (userIds, subDetails) => {
    const today = new Date().toISOString().split("T")[0];

    // Prepare the promises for all users
    const subscriptionPromises = userIds.map((userId) => {
      const payload = {
        user_id: userId,
        plan_id: subDetails.plan_id,
        package_id: subDetails.package_id,
        package_start_date: today,
        package_end_date: subDetails.end_date, // Using the end date from the org's active plan
        payment_mode: "Online",
        is_payment_success: 1,
        organization_id: selectedOrgId, // Or handle as needed
      };

      return fetch(SummaryApi.organizationUserSubscriptionCreate.url, {
        method: SummaryApi.organizationUserSubscriptionCreate.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).then((res) => res.json());
    });

    try {
      const results = await Promise.all(subscriptionPromises);
      const successCount = results.filter((r) => r.status).length;

      if (successCount > 0) {
        toast.success(`Activated subscriptions for ${successCount} users`, {
          icon: "🎟️",
          style: { borderRadius: "12px", background: "#333", color: "#fff" },
        });
      }
    } catch (error) {
      console.error("Bulk Subscription Error:", error);
      toast.error("Failed to link subscriptions to some users");
    }
  };
  const handleDownloadTemplate = () => {
    // Path starts from the public folder root
    const templateUrl = "/eveyasampleexcelformat.xlsx";

    const link = document.createElement("a");
    link.href = templateUrl;

    // This attribute forces the download and sets the filename the user sees
    link.setAttribute("download", "Eveya_Sample_Template.xlsx");

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 lg:p-12 font-sans text-slate-900">
      <div className="max-w-8xl mx-auto">
        {/* Header Section */}
        <header className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200">
              <FileSpreadsheet className="text-white" size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-800">
                Bulk Data Import
              </h1>
              <p className="text-slate-500 font-medium">
                Manage organization records and subscriptions
              </p>
            </div>
          </div>
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all shadow-sm active:scale-95"
          >
            <Download size={18} />
            Download Sample Template
          </button>
        </header>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column: Form & Upload */}
          <div className="lg:col-span-2 space-y-6">
            {/* Step 1: Org Selection */}
            <section className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-sm">
                  1
                </span>
                <h2 className="font-bold text-lg text-slate-700">
                  Select Target Organization
                </h2>
              </div>

              <div className="relative group">
                <Building2
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors"
                  size={20}
                />
                <select
                  value={selectedOrgId}
                  onChange={(e) => {
                    setSelectedOrgId(e.target.value);
                    setFile(null);
                  }}
                  className="w-full pl-12 pr-12 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all appearance-none font-semibold text-slate-700"
                >
                  <option value="">Choose an organization...</option>
                  {organizations.map((org) => (
                    <option
                      key={org.organization_id}
                      value={org.organization_id}
                    >
                      {org.organization_name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  size={20}
                />
              </div>
            </section>

            {/* Step 2: File Upload Zone */}
            <section className="bg-white rounded-3xl p-2 shadow-sm border border-slate-100 overflow-hidden">
              <div
                className={`relative border-2 border-dashed rounded-[2rem] transition-all min-h-[400px] flex flex-col items-center justify-center p-8 ${
                  isDragging
                    ? "border-indigo-500 bg-indigo-50/50"
                    : "border-slate-200 bg-transparent"
                } ${!selectedOrgId || (selectedOrgId && !hasActivePlan && !isLoadingSubs) ? "opacity-60 grayscale bg-slate-50" : ""}`}
              >
                {!selectedOrgId ? (
                  <div className="text-center space-y-4">
                    <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto text-slate-400">
                      <Lock size={32} />
                    </div>
                    <p className="text-slate-500 font-bold uppercase tracking-wider text-xs">
                      Waiting for organization selection
                    </p>
                  </div>
                ) : isLoadingSubs ? (
                  <div className="flex flex-col items-center gap-4">
                    <Loader2
                      className="animate-spin text-indigo-500"
                      size={40}
                    />
                    <p className="font-bold text-slate-500">
                      Verifying Permissions...
                    </p>
                  </div>
                ) : !hasActivePlan ? (
                  <div className="text-center max-w-sm">
                    <div className="w-20 h-20 bg-rose-50 text-rose-500 rounded-3xl flex items-center justify-center mx-auto mb-6 rotate-3">
                      <AlertCircle size={40} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      Subscription Required
                    </h3>
                    <p className="text-slate-500 text-sm mb-6">
                      This organization doesn't have an active plan. Please
                      upgrade to enable data imports.
                    </p>
                    <button className="px-6 py-2 bg-slate-900 text-white rounded-xl font-bold text-sm">
                      Contact Support
                    </button>
                  </div>
                ) : isUploading ? (
                  <div className="w-full max-w-md text-center space-y-8">
                    <div>
                      <div className="relative w-32 h-32 mx-auto mb-6">
                        <svg className="w-full h-full" viewBox="0 0 100 100">
                          <circle
                            className="text-slate-100 stroke-current"
                            strokeWidth="8"
                            fill="transparent"
                            r="40"
                            cx="50"
                            cy="50"
                          />
                          <circle
                            className="text-indigo-600 stroke-current transition-all duration-300 ease-out"
                            strokeWidth="8"
                            strokeLinecap="round"
                            fill="transparent"
                            r="40"
                            cx="50"
                            cy="50"
                            style={{
                              strokeDasharray: 251.2,
                              strokeDashoffset:
                                251.2 - (251.2 * uploadProgress) / 100,
                            }}
                          />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center font-black text-2xl text-slate-800">
                          {uploadProgress}%
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-slate-800">
                        Uploading Records...
                      </h3>
                      <p className="text-slate-500 text-sm mt-1">
                        Please do not close the browser tab
                      </p>
                    </div>
                  </div>
                ) : !file ? (
                  <div
                    className="text-center cursor-pointer group w-full h-full flex flex-col justify-center"
                    onClick={() => fileInputRef.current.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      validateAndSetFile(e.dataTransfer.files[0]);
                    }}
                  >
                    <div className="w-24 h-24 bg-indigo-50 text-indigo-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300 shadow-inner">
                      <UploadCloud size={40} />
                    </div>
                    <h3 className="text-2xl font-black text-slate-800 mb-2">
                      Drop your file here
                    </h3>
                    <p className="text-slate-400 font-medium">
                      Supports .xlsx, .xls and .csv formats
                    </p>
                    <div className="mt-8 flex items-center justify-center gap-2 text-indigo-600 font-bold text-sm">
                      <span>Or browse files</span>
                      <ArrowRight size={16} />
                    </div>
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full max-w-sm"
                  >
                    <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative shadow-2xl overflow-hidden">
                      <div className="absolute top-0 right-0 p-6 opacity-10">
                        <FileSpreadsheet size={120} />
                      </div>
                      <button
                        onClick={() => setFile(null)}
                        className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
                      >
                        <X size={18} />
                      </button>

                      <div className="relative z-10">
                        <div className="w-14 h-14 bg-emerald-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/20">
                          <FileCheck size={28} />
                        </div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                          File Ready
                        </p>
                        <h4 className="text-lg font-bold truncate mb-8">
                          {file.name}
                        </h4>

                        <button
                          onClick={handleUpload}
                          className="w-full bg-indigo-500 hover:bg-indigo-400 py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all shadow-lg shadow-indigo-500/25"
                        >
                          Process & Import
                          <ArrowRight size={18} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => validateAndSetFile(e.target.files[0])}
                className="hidden"
                accept=".xlsx, .xls, .csv"
              />
            </section>
          </div>

          {/* Right Column: Status & Info */}
          <aside className="space-y-6">
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={20} className="text-indigo-600" />
                  <h3 className="font-bold text-slate-800">
                    Organization Status
                  </h3>
                </div>
                {hasActivePlan && (
                  <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
                )}
              </div>

              <AnimatePresence mode="wait">
                {!selectedOrgId ? (
                  <div className="py-12 text-center space-y-3">
                    <HelpCircle size={40} className="mx-auto text-slate-200" />
                    <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">
                      No Org Selected
                    </p>
                  </div>
                ) : subscriptions.length > 0 ? (
                  <div className="space-y-4">
                    {subscriptions.map((sub) => (
                      <div
                        key={sub.organization_subscription_id}
                        className="relative group"
                      >
                        <div
                          className={`p-5 rounded-2xl border-2 transition-all ${sub.status === "Active" ? "border-emerald-100 bg-emerald-50/30" : "border-slate-100 bg-slate-50"}`}
                        >
                          <div className="flex items-center justify-between mb-4">
                            <div
                              className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${sub.status === "Active" ? "bg-emerald-500 text-white" : "bg-slate-300 text-slate-600"}`}
                            >
                              {sub.status}
                            </div>
                            <Package size={18} className="text-slate-300" />
                          </div>
                          <h4 className="font-black text-slate-800 uppercase text-xs truncate mb-1">
                            {sub.plan_name || "Standard Plan"}
                          </h4>
                          <p className="text-[11px] font-bold text-indigo-500 mb-4">
                            {sub.package_name || "Legacy Package"}
                          </p>

                          <div className="pt-4 border-t border-slate-200/50 flex items-center gap-2 text-slate-400">
                            <Calendar size={12} />
                            <span className="text-[10px] font-bold">
                              Expires:{" "}
                              {new Date(sub.end_date).toLocaleDateString(
                                "en-GB",
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 rounded-2xl bg-rose-50 border border-rose-100 text-center">
                    <AlertCircle
                      size={24}
                      className="mx-auto text-rose-500 mb-2"
                    />
                    <p className="text-xs font-bold text-rose-700">
                      No active subscriptions found for this organization.
                    </p>
                  </div>
                )}
              </AnimatePresence>
            </div>

            {/* Quick Tips */}
            <div className="bg-indigo-900 rounded-3xl p-6 text-white shadow-xl relative overflow-hidden">
              <div className="relative z-10">
                <h4 className="font-bold mb-4 flex items-center gap-2">
                  <Info size={18} className="text-indigo-300" />
                  Import Tips
                </h4>
                <ul className="space-y-3">
                  {[
                    "Check for duplicate phone numbers",
                    "Ensure columns match the template",
                  ].map((tip, i) => (
                    <li
                      key={i}
                      className="text-xs font-medium text-indigo-100 flex items-start gap-2"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1 shrink-0"></span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="absolute -bottom-6 -right-6 text-white/5 rotate-12">
                <FileSpreadsheet size={120} />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

/* --- ENHANCED UPLOAD RESULT TOAST --- */
const UploadResultToast = ({
  t,
  createdCount,
  duplicateCount,
  duplicateData,
  message,
}) => {
  const isAllDuplicate = createdCount === 0 && duplicateCount > 0;

  return (
    <motion.div
      initial={{ x: 300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      className={`max-w-md w-full bg-white shadow-2xl rounded-3xl overflow-hidden border ${isAllDuplicate ? "border-amber-100" : "border-emerald-100"}`}
    >
      <div className="p-5 flex items-center justify-between border-b border-slate-50">
        <div className="flex items-center gap-4">
          <div
            className={`h-12 w-12 rounded-2xl flex items-center justify-center ${isAllDuplicate ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"}`}
          >
            {isAllDuplicate ? <AlertCircle /> : <CheckCircle2 />}
          </div>
          <div>
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">
              {isAllDuplicate ? "Duplicate Encountered" : "Import Successful"}
            </h3>
            <p className="text-[10px] font-bold text-slate-400">{message}</p>
          </div>
        </div>
        <button
          onClick={() => toast.dismiss(t.id)}
          className="p-2 hover:bg-slate-50 rounded-full transition-colors text-slate-400"
        >
          <X size={18} />
        </button>
      </div>

      <div className="p-5 bg-slate-50/50">
        <div className="flex gap-4 mb-4">
          <div className="flex-1 bg-white p-3 rounded-2xl border border-slate-100 text-center">
            <p className="text-[10px] font-bold text-slate-400 uppercase">
              Created
            </p>
            <p className="text-lg font-black text-emerald-600">
              {createdCount}
            </p>
          </div>
          <div className="flex-1 bg-white p-3 rounded-2xl border border-slate-100 text-center">
            <p className="text-[10px] font-bold text-slate-400 uppercase">
              Duplicates
            </p>
            <p className="text-lg font-black text-amber-600">
              {duplicateCount}
            </p>
          </div>
        </div>

        {duplicateCount > 0 && (
          <div className="space-y-2">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-1">
              Skipped Entries
            </p>
            <div className="max-h-32 overflow-y-auto space-y-1 pr-2 custom-scrollbar">
              {duplicateData.map((user, idx) => (
                <div
                  key={idx}
                  className="bg-white border border-slate-100 rounded-xl px-3 py-2 flex justify-between items-center shadow-sm"
                >
                  <span className="text-[10px] font-bold text-slate-700 truncate">
                    {user.user_name}
                  </span>
                  <span className="text-[9px] font-black text-slate-400 font-mono">
                    {user.mobile_number}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default UploadExcel;
